import { useEffect, useState } from 'react';
import { Drawer } from '@mui/material';
import { IconSparkles, IconX } from '@tabler/icons-react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { Button, InfoRow, Textarea } from 'ui';

import { GALLERY_MAX_DESCRIPTION_LENGTH, formatFileSize } from '../../core/utils/images';

const formatAdded = (createdAt) => {
  const seconds = Number(createdAt);
  if (!Number.isFinite(seconds) || seconds <= 0) return '-';
  return format(new Date(seconds * 1000), 'd MMM yyyy, h:mm a');
};

/**
 * Detail drawer for the selected image, following the same chrome as
 * `ContactDrawer` and `ui`'s `CampaignDrawer`: a right-anchored MUI drawer, a
 * 600px column, a borderless Close button above the heading, and `InfoRow` for
 * the metadata pairs.
 *
 * The description editor is the point of the panel, so it sits directly under the
 * preview rather than below the metadata.
 */
const ImageDetailsDrawer = ({ open, image, saving, describing, onClose, onSave, onGenerate }) => {
  const [description, setDescription] = useState(image?.description ?? '');

  // Re-seed when a different tile is selected without closing the drawer, and when
  // a generated description arrives for the image already open.
  useEffect(() => {
    setDescription(image?.description ?? '');
  }, [image?.imageId, image?.description]);

  if (!image) return null;

  const dirty = description !== (image.description ?? '');
  const disabled = saving || describing;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ position: 'relative' }}>
      <div className="h-full w-[600px] space-y-4 overflow-y-auto p-6" data-testid="image-details-drawer">
        <Button
          title="Close"
          size="sm"
          LeftIcon={IconX}
          iconProps={{ size: 20 }}
          onClick={onClose}
          className="
              border-0 
              !p-0 
              !text-gray-600 
              !shadow-none 
              focus:!ring-0 
              enabled:hover:!text-gray-500
              enabled:focus:!text-gray-500
              enabled:active:!text-gray-700
            "
          disabled={saving}
        />

        <div className="relative space-y-4">
          <h3 className="text-h3 font-semibold" data-testid="image-details-drawer-heading">
            Image details
          </h3>

          <img
            src={image.url}
            alt={image.description || image.filename}
            className="max-h-[280px] w-full rounded-lg bg-gray-10 object-contain"
          />

          <div>
            <Textarea
              id="image-description"
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              maxLength={GALLERY_MAX_DESCRIPTION_LENGTH}
              disabled={describing}
              size="lg"
            />

            <div className="mt-3 flex items-center justify-between gap-3">
              <Button
                title={describing ? 'Writing description…' : 'Generate description'}
                size="sm"
                LeftIcon={IconSparkles}
                iconProps={{ size: 18 }}
                onClick={onGenerate}
                disabled={disabled}
              />
              <Button
                title="Save description"
                size="sm"
                color="primary"
                onClick={() => onSave(description)}
                disabled={disabled || !dirty}
              />
            </div>
          </div>

          <InfoRow primaryLabel="File" value={image.filename || '-'} />

          <InfoRow primaryLabel="Dimensions" value={`${image.width}×${image.height}`} />

          <InfoRow primaryLabel="Size" value={formatFileSize(image.size) || '-'} />

          <InfoRow primaryLabel="Added" value={formatAdded(image.createdAt)} />
        </div>
      </div>
    </Drawer>
  );
};

ImageDetailsDrawer.propTypes = {
  open: PropTypes.bool,
  image: PropTypes.shape({
    imageId: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    filename: PropTypes.string,
    description: PropTypes.string,
    descriptionSource: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    size: PropTypes.number,
    createdAt: PropTypes.string,
  }),
  saving: PropTypes.bool,
  describing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};

ImageDetailsDrawer.defaultProps = {
  open: false,
  image: null,
  saving: false,
  describing: false,
};

export default ImageDetailsDrawer;
